import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import type { LeaveBalance, LeaveRequestStatus, LeaveTypeRef } from '@mnc/contracts';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';
import { stripBidiMarks } from '../../lib/text';

type Tone = 'neutral' | 'brand' | 'warn' | 'danger' | 'info';
export const STATUS_TONE: Record<LeaveRequestStatus, Tone> = { pending: 'warn', pending_hr: 'info', approved: 'brand', rejected: 'danger', cancelled: 'neutral', cancel_pending: 'warn' };

/** Hours are the unit; days are how people think: both, with the contract's own hours per day. */
export function useHours() {
  const { t, i18n } = useTranslation();
  const n = (v: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(v);
  return {
    n,
    hours: (h: number) => t('leave.hoursValue', { hours: n(h) }),
    days: (d: number) => t('leave.daysValue', { days: n(d) }),
    both: (h: number, hoursPerDay: number) => `${t('leave.daysValue', { days: n(Math.round((h / hoursPerDay) * 100) / 100) })} · ${t('leave.hoursValue', { hours: n(h) })}`,
  };
}

export function TypeDot({ type, className }: { type: Pick<LeaveTypeRef, 'colour' | 'name_en' | 'name_ar'>; className?: string }) {
  const { i18n } = useTranslation();
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: type.colour }} />
      <span className="truncate" dir="auto">{pick(i18n.language, type.name_en, type.name_ar)}</span>
    </span>
  );
}

export function StatusBadge({ status }: { status: LeaveRequestStatus }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`leave.statuses.${status}`)}</Badge>;
}

export const fmtDate = (iso: string | null, language: string, fallback = '—') => (iso ? stripBidiMarks(new Date(`${iso}T00:00:00Z`).toLocaleDateString(language, { dateStyle: 'medium', timeZone: 'UTC' })) : fallback);
export const fmtPeriod = (start: string, end: string, language: string) => (start === end ? fmtDate(start, language) : `${fmtDate(start, language)} → ${fmtDate(end, language)}`);

/**
 * The balance as one picture: what is available to request, and the identity it comes from
 * (total = used + balance + not yet accrued) so a large entitlement beside a small balance explains itself.
 */
export function BalanceCard({ balance, compact }: { balance: LeaveBalance; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const h = useHours();
  const hpd = balance.contract.hours_per_day;
  const total = Math.max(1, balance.total_hours);
  const part = (v: number) => `${Math.max(0, Math.min(100, (v / total) * 100))}%`;
  const rows: Array<[string, number, string?]> = [
    [t('leave.entitlement'), balance.entitlement_hours],
    [t('leave.opening'), balance.opening_hours],
    [t('leave.carriedOver'), balance.carried_over_hours],
    [t('leave.adjustments'), balance.adjustment_hours],
    [t('leave.expired'), -balance.expired_hours],
  ].filter(([, v], i) => i === 0 || v !== 0) as Array<[string, number]>;
  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label">{t('leave.available')}</div>
          <div className={cn('text-3xl font-semibold tabular-nums', balance.available_hours < 0 && 'text-red-600')}>{h.days(balance.available_days)}</div>
          <div className="text-sm text-neutral-500">{h.hours(balance.available_hours)} · {t('leave.asOf', { date: fmtDate(balance.as_of, i18n.language) })}</div>
        </div>
        <div className="text-end text-xs text-neutral-500">
          <div dir="ltr">{balance.contract.reference}</div>
          <div>{pick(i18n.language, balance.terms.policy.name_en, balance.terms.policy.name_ar)} · {t('leave.daysPerYear', { days: h.n(balance.terms.annual_days) })}</div>
          {balance.in_probation && <div className="mt-1 inline-flex items-center gap-1 text-amber-600"><Lock size={12} />{t('leave.inProbation')}</div>}
        </div>
      </div>
      <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800" dir="ltr" title={t('leave.identity')}>
        <div className="bg-neutral-500" style={{ width: part(balance.used_hours) }} />
        <div className="bg-blue-500" style={{ width: part(balance.scheduled_hours) }} />
        <div className="bg-amber-400" style={{ width: part(balance.pending_hours) }} />
        <div className="bg-brand-500" style={{ width: part(Math.max(0, balance.available_hours)) }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600 dark:text-neutral-300">
        <Legend colour="bg-neutral-500" label={t('leave.used')} value={h.both(balance.used_hours, hpd)} />
        <Legend colour="bg-blue-500" label={t('leave.scheduled')} value={h.both(balance.scheduled_hours, hpd)} />
        <Legend colour="bg-amber-400" label={t('leave.pending')} value={h.both(balance.pending_hours, hpd)} />
        <Legend colour="bg-brand-500" label={t('leave.available')} value={h.both(balance.available_hours, hpd)} />
        <Legend colour="bg-neutral-200 dark:bg-neutral-700" label={t('leave.notYetAccrued')} value={h.both(balance.not_yet_accrued_hours, hpd)} />
      </div>
      {!compact && (
        <dl className="mt-3 grid gap-x-6 gap-y-1 border-t border-neutral-100 pt-3 text-sm dark:border-neutral-800 sm:grid-cols-2">
          {rows.map(([label, v]) => <div key={label} className="flex justify-between gap-2"><dt className="text-neutral-500">{label}</dt><dd className="tabular-nums">{h.hours(v)}</dd></div>)}
          <div className="flex justify-between gap-2"><dt className="text-neutral-500">{t('leave.accrued')}</dt><dd className="tabular-nums">{h.hours(balance.accrued_hours)}</dd></div>
          {balance.attendance_hours > 0 && <div className="flex justify-between gap-2"><dt className="text-neutral-500">{t('leave.attendanceIncluded')}</dt><dd className="tabular-nums">{h.hours(balance.attendance_hours)}</dd></div>}
          <div className="flex justify-between gap-2 font-medium sm:col-span-2"><dt>{t('leave.identity')}</dt><dd className="tabular-nums" dir="ltr">{h.n(balance.total_hours)} = {h.n(balance.used_hours)} + {h.n(balance.balance_hours)} + {h.n(balance.not_yet_accrued_hours)}</dd></div>
        </dl>
      )}
    </div>
  );
}

function Legend({ colour, label, value }: { colour: string; label: string; value: string }) {
  return <span className="inline-flex items-center gap-1.5"><span className={cn('h-2 w-2 rounded-full', colour)} />{label}: <span className="tabular-nums">{value}</span></span>;
}
